---
title: 'Step 3: Configure the Collector'
install_track_title: Installation Guide (Google Cloud SQL and AlloyDB)
backlink_href: /docs/install
backlink_title: 'Installation Guide'
---

import PublicLastStepLogInsightsLink from '../_public_last_step_log_insights_link.mdx'

export const APIKeyInstructions = ({ apiKey }) => {
  return apiKey ? (
    <React.Fragment>
      The <code>PGA_API_KEY</code> for the current organization is{" "}
      <code>{apiKey}</code>
    </React.Fragment>
  ) : (
    <React.Fragment>
      The <code>PGA_API_KEY</code> can be found in the pganalyze Settings page
      for your organization, under the API Access tab
    </React.Fragment>
  );
};

export const CollectorDotEnv = ({ apiKey }) => {
  return (
    <CodeBlock>
      {`PGA_API_KEY=${apiKey || "your-organization-api-key"}
DB_HOST=1.2.3.4
DB_NAME=your_database_name
DB_USERNAME=your_monitoring_user
DB_PASSWORD=your_monitoring_user_password
GCP_PROJECT_ID=your_gcp_project_id
GCP_CLOUDSQL_INSTANCE_ID=your_gcp_cloudsql_instance_id`}
    </CodeBlock>
  );
};

## Configuring the collector with Docker

Create your configuration file (e.g. named `pganalyze_collector.env`) with environment variables like this:

<CollectorDotEnv apiKey={props.apiKey} />

Fill in the values step-by-step:

1. <APIKeyInstructions apiKey={props.apiKey} />
2. The `DB_HOST` is the IP address of your Google Cloud SQL / AlloyDB instance
3. The `DB_NAME` is the database on the Google Cloud SQL / AlloyDB instance you want to monitor
4. The `DB_USERNAME` and `DB_PASSWORD` should be the credentials of the monitoring user we created in Step 1 - if using IAM Authentication see below
5. The `GCP_PROJECT_ID` should match the name of the GCP project that contains your Cloud SQL / AlloyDB instance
6. The `GCP_CLOUDSQL_INSTANCE_ID` should match the name of the Cloud SQL instance - if using AlloyDB see below

### Using IAM Authentication

If you're using IAM Authentication, the configuration file looks like the following instead:

<CodeBlock>
  {`PGA_API_KEY=${props.apiKey || "your-organization-api-key"}
DB_NAME=your_database_name
DB_USERNAME=your_monitoring_user
GCP_PROJECT_ID=your_gcp_project_id
GCP_CLOUDSQL_INSTANCE_ID=your_gcp_cloudsql_instance_id
DB_USE_IAM_AUTH=true
GCP_REGION=your_gcp_region`}
</CodeBlock>

Note that you don't need `DB_PASSWORD` and `DB_HOST` with IAM Authentication, as there is no password for a monitoring user,
and the host name will be specified using the project ID, the region, and the instance ID.

Instead of specifying `GCP_REGION`, you can also update `GCP_CLOUDSQL_INSTANCE_ID` with the shape of `project_id:region:instance_id`.

<CodeBlock>
  {`PGA_API_KEY=${props.apiKey || "your-organization-api-key"}
DB_NAME=your_database_name
DB_USERNAME=your_monitoring_user
GCP_CLOUDSQL_INSTANCE_ID=your_gcp_project_id:your_gcp_region:your_gcp_cloudsql_instance_id
DB_USE_IAM_AUTH=true`}
</CodeBlock>

### Instructions for Google AlloyDB

If you are using Google AlloyDB, do not specify `GCP_CLOUDSQL_INSTANCE_ID`, but instead specify `GCP_ALLOYDB_CLUSTER_ID` (set to the name of the cluster) and `GCP_ALLOYDB_INSTANCE_ID` (set to the instance name within the cluster).

## Test snapshot

Then run the following:

<CodeBlock language="bash">
{`docker run --env-file pganalyze_collector.env quay.io/pganalyze/collector:stable test`}
</CodeBlock>

<PublicLastStepLogInsightsLink />
